import React, { useEffect, useRef, useState } from 'react';
import { MAX_NICKNAME_LENGTH } from '../constants/form';
import { auth, database } from '../api/firebase';
import { get, set, ref } from '@firebase/database';
import { useHistory } from 'react-router';
import { signInAnonymously } from '@firebase/auth';
import ErrorText, { INTERNAL_SERVER_ERROR_TEXT } from './ErrorText';

interface FormJoinBubbleProps {
  requestedRoomcode?: string;
}

const FormJoinBubble: React.FC<FormJoinBubbleProps> = ({ requestedRoomcode }) => {
  const history = useHistory();
  const [roomcode, setRoomcode] = useState<string>(requestedRoomcode || '');
  const [nickname, setNickname] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const inputRoomcodeRef = useRef<HTMLInputElement>(null);
  const inputNicknameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    requestedRoomcode
      ? inputNicknameRef.current!.focus()
      : inputRoomcodeRef.current!.focus();
  }, [requestedRoomcode])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setIsFetching(true);
      setError('');
      const creds = await signInAnonymously(auth);
      const success = await tryToJoinRoom(creds.user.uid);
      if (success) {
        history.push(`/${roomcode}`);
      } else {
        setError('enter a valid room code');
      }
    } catch (error) {
      console.log(error);
      setError(INTERNAL_SERVER_ERROR_TEXT);
    } finally {
      setIsFetching(false);
    }
  };

  const tryToJoinRoom = async (userId: string): Promise<boolean> => {
    if (await roomExists(roomcode)) {
      await joinRoom(userId, nickname, roomcode);
      return true;
    } else {
      return false;
    }
  };

  const roomExists = async (roomcode: string): Promise<boolean> => {
    const snapshot = await get(ref(database, `bubbles/${roomcode}`));
    return snapshot.exists();
  };

  const joinRoom = async (userId: string, nickname: string, roomcode: string): Promise<void> => {
    await set(ref(database, `bubbles/${roomcode}/users/${userId}`), { nickname });
  };

  return (
    <div>
      <form className="flex flex-col justify-center" onSubmit={handleSubmit}>
        <p className="text-white text-2xl select-none">roomcode</p>
        <input
          ref={inputRoomcodeRef}
          type="text"
          value={roomcode}
          onChange={(e) => setRoomcode(e.target.value)}
          disabled={isFetching || !!requestedRoomcode}
          className={
            "text-white border-none outline-none h-8 m-0 p-2 rounded-xl bg-black bg-opacity-10 " +
            `${isFetching ? 'cursor-not-allowed' : 'cursor-auto'}`
          }
        />
        <p className="text-white mt-6 text-2xl select-none">nickname</p>
        <input
          ref={inputNicknameRef}
          className={
            "text-white border-none outline-none h-8 m-0 p-2 rounded-xl bg-black bg-opacity-10 " +
            `${isFetching ? 'cursor-not-allowed' : 'cursor-auto'}`
          }
          type="text"
          value={nickname}
          onChange={(e) => setNickname(e.target.value.trimLeft())}
          maxLength={MAX_NICKNAME_LENGTH}
          disabled={isFetching}
        />
        <button
          disabled={!nickname || !roomcode || isFetching}
          className={
            "border-none mt-6 h-8 w-full text-white bg-blue-400 rounded-xl outline-none " +
            "disabled:bg-gray-500 disabled:cursor-not-allowed"
          }
        >
          join
        </button>
      </form>
      {error && <ErrorText message={error} />}
    </div>
  );
};

export default FormJoinBubble;
